import { apiClient } from './apiClient';

export type LeadUpsellStatus =
  | 'identified'
  | 'proposed'
  | 'interested'
  | 'won'
  | 'lost';

export type LeadUpsellType =
  | 'add_on_service'
  | 'premium_upgrade'
  | 'expanded_scope'
  | 'additional_location'
  | 'extra_users'
  | 'custom';

export interface LeadUpsellOpportunity {
  id: string;
  tenant: string;
  client: string;
  title: string;
  description: string;
  upsell_type: LeadUpsellType;
  upsell_type_display: string;
  estimated_value: string;
  status: LeadUpsellStatus;
  status_display: string;
  probability: number | null;
  expected_close_date: string | null;
  notes: string;
  archived_at: string | null;
  created_by: string | null;
  created_by_name: string | null;
  updated_by: string | null;
  updated_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface LeadUpsellWritePayload {
  title: string;
  description?: string;
  upsell_type: LeadUpsellType;
  estimated_value: string;
  status: LeadUpsellStatus;
  probability?: number | null;
  expected_close_date?: string | null;
  notes?: string;
}

export interface LeadUpsellClientSummary {
  base_lead_value: string;
  potential_upsell_value: string;
  won_upsell_value: string;
  total_potential_deal_value: string;
  active_upsell_count: number;
}

export interface LeadUpsellReporting {
  total_opportunities: number;
  total_potential_upsell_revenue: string;
  total_won_upsell_revenue: string;
  by_status: { status: string; count: number; value_sum: string }[];
  by_type: { upsell_type: string; count: number; value_sum: string }[];
}

export const leadUpsellService = {
  async listForClient(clientId: string): Promise<{ results: LeadUpsellOpportunity[] }> {
    const data = await apiClient.get(`/clients/${clientId}/lead-upsells/`);
    if (Array.isArray(data)) return { results: data };
    return { results: (data as { results?: LeadUpsellOpportunity[] }).results ?? [] };
  },

  async getSummary(clientId: string): Promise<LeadUpsellClientSummary> {
    return apiClient.get(`/clients/${clientId}/lead-upsells/summary/`);
  },

  async create(clientId: string, payload: LeadUpsellWritePayload): Promise<LeadUpsellOpportunity> {
    return apiClient.post(`/clients/${clientId}/lead-upsells/`, payload);
  },

  async update(
    clientId: string,
    upsellId: string,
    payload: Partial<LeadUpsellWritePayload>
  ): Promise<LeadUpsellOpportunity> {
    return apiClient.patch(`/clients/${clientId}/lead-upsells/${upsellId}/`, payload);
  },

  async archive(clientId: string, upsellId: string): Promise<void> {
    return apiClient.delete(`/clients/${clientId}/lead-upsells/${upsellId}/`);
  },

  async getTenantReporting(): Promise<LeadUpsellReporting> {
    return apiClient.get('/lead-upsells/reporting/');
  },
};
